import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle2, ExternalLink, RefreshCw } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useProviderTermsSettings, useUpdateProviderTermsSettings } from '@/hooks/provider/useProviderTermsSettings';
import { ProviderTermsRequestError, normalizeProviderTermsUrl } from '@/services/provider/terms.service';

type FormState = {
  termsUrl: string;
};

type FormErrors = {
  termsUrl?: string;
};

const MAX_LENGTH = 2048;

function isValidHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && Boolean(url.host);
  } catch {
    return false;
  }
}

export function ProviderTermsSettingsCard() {
  const { t } = useTranslation();
  const settingsQuery = useProviderTermsSettings();
  const updateMutation = useUpdateProviderTermsSettings();

  const [formState, setFormState] = useState<FormState>({ termsUrl: '' });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (settingsQuery.data) {
      setFormState({ termsUrl: settingsQuery.data.termsUrl ?? '' });
    }
  }, [settingsQuery.data]);

  const isSaving = updateMutation.isPending;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalized = normalizeProviderTermsUrl(formState.termsUrl);
    const nextErrors: FormErrors = {};

    if (normalized) {
      if (normalized.length > MAX_LENGTH) {
        nextErrors.termsUrl = t(
          'settings.terms.validation.tooLong',
          'Terms URL must be shorter than 2048 characters.',
        );
      } else if (!isValidHttpsUrl(normalized)) {
        nextErrors.termsUrl = t(
          'settings.terms.validation.invalid',
          'Enter a valid HTTPS URL.',
        );
      }
    }

    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    try {
      const updated = await updateMutation.mutateAsync({ termsUrl: normalized });
      setFormState({ termsUrl: updated.termsUrl ?? '' });
      toast.success(
        t('settings.terms.saveSuccess', 'Terms & Conditions link saved.'),
      );
    } catch (error) {
      if (error instanceof ProviderTermsRequestError && error.errors?.terms_url?.[0]) {
        setFormErrors({ termsUrl: error.errors.terms_url[0] });
      }
      const message =
        error instanceof Error && error.message
          ? error.message
          : t('settings.terms.saveError', 'Unable to save Terms & Conditions link.');
      toast.error(message);
    }
  };

  if (settingsQuery.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
            {t('settings.terms.title', 'Terms & Conditions')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.terms.description',
            'Provide a link to your Terms & Conditions page for customer flows.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-32" />
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            {t('settings.terms.title', 'Terms & Conditions')}
          </CardTitle>
          <CardDescription>
            {t(
              'settings.terms.description',
              'Provide a link to your Terms & Conditions page for provider and customer flows.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTitle>
              {t('settings.terms.loadError', 'Unable to load Terms & Conditions settings.')}
            </AlertTitle>
            <AlertDescription className="mt-2 flex items-center gap-3">
              <span>{settingsQuery.error.message}</span>
              <Button variant="secondary" size="sm" onClick={() => settingsQuery.refetch()} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                {t('common.retry', 'Retry')}
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const hasValue = (settingsQuery.data?.termsUrl ?? null) !== null;
  const currentUrl = settingsQuery.data?.termsUrl;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
          {t('settings.terms.title', 'Terms & Conditions')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.terms.description',
            'Provide a link to your Terms & Conditions page for customer flows.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {hasValue && currentUrl ? (
          <div className="mb-4 inline-flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm">
            <ExternalLink className="h-4 w-4" />
            <a
              href={currentUrl}
              className="truncate text-primary underline"
              target="_blank"
              rel="noreferrer"
            >
              {currentUrl}
            </a>
          </div>
        ) : null}
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('settings.terms.fields.link', 'Terms & Conditions URL')}
            </label>
            <Input
              value={formState.termsUrl}
              placeholder={t('settings.terms.placeholders.link', 'https://example.com/terms')}
              onChange={(event) => setFormState({ termsUrl: event.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              {t(
                'settings.terms.helper',
                "Link users to your website's Terms & Conditions. HTTPS only; leave blank to disable.",
              )}
            </p>
            {formErrors.termsUrl ? (
              <p className="text-sm text-destructive">{formErrors.termsUrl}</p>
            ) : null}
          </div>
          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setFormState({ termsUrl: '' })}
              disabled={isSaving}
            >
              {t('common.clear', 'Clear')}
            </Button>
            <Button type="submit" disabled={isSaving} className="gap-2">
              {isSaving && <RefreshCw className="h-4 w-4 animate-spin" />}
              {t('settings.terms.actions.save', 'Save link')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
